import { isBefore, startOfDay } from 'date-fns';
import { action, computed, makeObservable, observable, when } from 'mobx';
import { rootStore } from 'src/app';
import i18next from 'src/i18n';
import { DataType, IQuestion, ItemStatus, QuestionPosition, UserRole } from '../types';
import { Item, User, Comment } from './';
import { computedFn } from 'mobx-utils';


export class Question extends Item<IQuestion> implements IQuestion {
  answered:       number   = null;
  child_id?:      number   = null;
  parent_id?:     number   = null;
  meeting_id?:    number   = null;
  processed:      boolean  = null;
  postponed?:     Date     = null;
  critical:       boolean  = null;
  decided:        boolean  = null;
  from?:          number   = null;
  to?:            number   = null;
  position:       QuestionPosition = null;
  protected constructor(data: IQuestion) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.Question, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually 
    makeObservable(this, {
      answered:      observable,
      meeting_id:    observable,
      processed:     observable,
      postponed:     observable,
      critical:      observable,
      position:      observable,
      decided:       observable,
      from:          observable,
      to:            observable,
      child_id:      observable,
      parent_id:     observable,

      getMeetingId:         computed,
      getFrom:              computed,
      getTo:                computed,
      isRelevantForMeeting: computed,
      isRemitable:          computed,

      handleInMeeting:  action,
      remit:            action,
      cancelRemittance: action,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here. 
    this.fromData(data);
  }

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/

  public static create(data?: IQuestion): Question {
    const nextMeeting = rootStore.meetingStore.getNextMeeting;

    return new Question(data || {
      ...Item.create(data).toData(),
      title:        "",
      answered:     null,
      position:     rootStore.meetingStore.activeMeeting ? QuestionPosition.During: QuestionPosition.Before,
      critical:     false,
      processed:    false,
      decided:      false,
      from :        rootStore.authStore.getCurUser.getRepresentative(rootStore.authStore.getCurSpaceID),
      to:           null,
      workspace_id: rootStore.authStore.getCurWorkspaceID,
      room_id:      rootStore.authStore.getCurRoomID,
      date:         nextMeeting && nextMeeting.date ? nextMeeting.date : new Date(),
    });
  }

  protected fromData(data: IQuestion): void {
    super.fromData(data);
    this.child_id      = data.child_id   && Number(data.child_id);
    this.parent_id     = data.parent_id  && Number(data.parent_id);
    this.answered      = data.answered   && Number(data.answered);
    this.meeting_id    = data.meeting_id && Number(data.meeting_id);
    this.processed     = data.processed;
    this.postponed     = data.postponed && new Date(data.postponed);
    this.critical      = data.critical;
    this.position      = data.position;
    this.decided       = data.decided;
    this.from          = data.from && Number(data.from);
    this.to            = data.to && Number(data.to);
  }

  public toData(): any {
    return {
        ...super.toData(),
        answered:   this.answered,
        child_id:   this.child_id,
        critical:   this.critical,
        decided:    this.decided,
        from:       this.from,
        meeting_id: this.meeting_id,
        parent_id:  this.parent_id,
        position:   this.position,
        postponed:  this.postponed && this.postponed.toISOString(),
        processed:  this.processed,
        to:         this.to,
    };
  }

  public toPatchData(patch: Partial<IQuestion>): any {
    return {
        ...super.toPatchData(patch),
        child_id:   "child_id"   in patch ? patch.child_id : undefined,
        parent_id:  "parent_id"  in patch ? patch.parent_id : undefined,
        answered:   "answered"   in patch ? patch.answered : undefined,
        meeting_id: "meeting_id" in patch ? patch.meeting_id : undefined,
        processed:  "processed"  in patch ? patch.processed : undefined,
        postponed:  "postponed"  in patch ? patch.postponed && patch.postponed.toISOString() : undefined,
        critical:   "critical"   in patch ? patch.critical : undefined,
        position:   "position"   in patch ? patch.position : undefined,
        decided:    "decided"    in patch ? patch.decided : undefined,
        from:       "from"       in patch ? patch.from : undefined,
        to:         "to"         in patch ? patch.to : undefined,
    };
  }

  get getStatus() {
    if(!this.id) return ItemStatus.New;
    if(this.answered) return ItemStatus.Answered;
    if(this.critical) return ItemStatus.Critical;
    return ItemStatus.Active;
  }

  get getDisplayPath(): string {
    return `${super.getDisplayPath} > ${i18next.t(this.position)}`;
  }

  /**
   * A question can be edited if
   * - it is not new (i.e. it has an ID)
   * - it is not decided (decided only allows revoking the decision)
   * - it is not a remitted parent
   */
  public get canEdit(): boolean {
    return Boolean(this.id) && !this.decided && !this.child_id && !this.archived;
  }

  public canEditField = computedFn((field: string = 'asd'): boolean => {
    const active = Boolean(this.id) && !this.decided && !this.child_id && !this.archived;
    const user_id = rootStore.authStore.getCurUserID;
    const leader = rootStore.authStore.isLeader;
    if(!field) return active;
    switch(field) {
      case 'answered' : 
      return active && ([this.from].includes(user_id) || leader);
      default : 
      return active;
    }
  });
  /*******************************************************************************************************************************************
   * Getter
   ********************************************************************************************************************************************/
  get isRelevantForMeeting(): boolean {
    return ([QuestionPosition.Board, QuestionPosition.Before].includes(this.position) && !this.decided) || this.processed;
  }

  get getMeetingId(): number {
    return this.meeting_id;
  }

  get getFrom(): User | null {
    return this.from ? rootStore.userStore.getItemByID(this.from) : null;
  }

  get getTo(): User | null {
    return this.to ? rootStore.userStore.getItemByID(this.to) : null;
  }

  get isCritical(): boolean {
    return !this.answered && this.critical;
  }

  get isPrio(): boolean {
    return !this.answered && !this.critical && isBefore(this.date, startOfDay(new Date()));
  }

  get getAnswer(): Comment {
    if(!this.answered) return null;

    return rootStore.commentStore.container[this.answered] || null;
  }

  get isRemitable(): Boolean {
    return !this.answered && !this.decided && this.position === QuestionPosition.Board;
  }

  /*******************************************************************************************************************************************
   * Setter
   ********************************************************************************************************************************************/
  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/
  /**
   * Either move or remit question to @room_id or creates a child question
   * 
   * @param room_id  target room within the current project the user has access to 
   * @param relocate If <true> moves the question, creates a child question otherwise
   */
  remit = async (room_id: number, relocate?: boolean): Promise<boolean> => {
    this.position = QuestionPosition.Remitted;

    // Move the whole question:
    if(relocate) {
      // Validity checks regarding the room_id are done in the backend
      try {
        await this.set("room_id", room_id, true);
      } catch (err) {
        console.error("Failed to relocate question", err);
        return false;
      }
    } else { // Create child question
      const child = Question.create(this.toData());
      child.id        = undefined;
      child.workspace_id = this.workspace_id;
      child.room_id   = room_id;
      child.parent_id = this.id;
      child.position  = QuestionPosition.Board;
      try {
        await child.save();
      } catch (err) {
        console.error("Failed to create remitted child question", err);
        return false;
      }
    }
    return true;
  }

  /**
   * Unlinks the child question created earlier by @remit 
   * To unlink, the child_id is set back to null. The backend will set the position to board.
   * The child question will remain in the system and its parent link will be set to null as well. 
   */
  cancelRemittance = () => {
    this.save({child_id: null});
  }

  /**
   * Change position to either during or before meeting depending on whether a meeting is running.
   */
  handleInMeeting = () => {
    this.set("position", rootStore.meetingStore.activeMeeting ? QuestionPosition.During : QuestionPosition.Before, true);
  }

  public validate = async (asBoolean?: boolean): Promise<any> => {
    let parentInvalids = await super.validate();
    if(this.isNew)   return [];
    if(this.decided) return [];
    
    // type this object, make generic for items and let each items contribute to list of required fields
    const obj: any = {
      to: this.to,
      from: this.from,
      date: this.date,
    };
    
    const itemInvalids = Object.keys(obj).filter((k) => {
      return !obj[k];
    });

    console.log(itemInvalids);

    const invalids = [...parentInvalids, ...itemInvalids]

    return asBoolean ? invalids.length : invalids;
  }
}